import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { fieldLabel } from '@/lib/form-styles'
import { apiErrorText } from '@/lib/api-error'
import { Modal } from '@symphenia/ui-components'
import {
  dailyRecordsApi,
  timeConfigApi,
} from '../api/time-tracking.api'
import type {
  DailyRecord,
  CustomFieldSchema,
  DailyRecordStatus,
  DayFieldDef,
  SelectionDataRecord,
} from '../types/time-tracking.types'
import { fmtTimeUTC } from '@/lib/format-date'
import { formatHours, useHoursFormat } from '@/lib/hours-format'

interface DailyRecordDialogProps {
  open: boolean
  onClose: () => void
  date: string // ISO date string YYYY-MM-DD
  onSaved?: () => void
  // Inline-Modus: als Reiter-Inhalt ohne Modal-Rahmen rendern (Abbrechen entfällt).
  inline?: boolean
  // Zielmitarbeiter (nur MANAGER+). Leer/undefined → angemeldeter Nutzer.
  userId?: string
  /** AP-24: Stundenblock zeigen (händische Erfassung für diesen Mitarbeiter). */
  manualHours?: boolean
  /**
   * AP-26: Uhrzeiten- und Montagefelder, die der Admin im Monatsbericht
   * eingeschaltet hat. Leer = der Block entfällt. Kommt aus `clock-settings`
   * (nie aus `GET /time-config` — ADMIN-only, KA-03/KA-08).
   */
  dayFields?: DayFieldDef[]
}

/**
 * AP-24: Stundeneingabe. Nimmt „8,5" / „8.5" und „8:30" gleichermaßen an —
 * unabhängig vom eingestellten Anzeigeformat. Leere Eingabe = nicht erfasst.
 * `undefined` bedeutet: Eingabe unverständlich.
 */
function parseHoursInput(raw: string): number | null | undefined {
  const v = raw.trim()
  if (v === '') return null
  if (v.includes(':')) {
    const [h, m] = v.split(':')
    const hh = Number(h)
    const mm = Number(m)
    if (!Number.isFinite(hh) || !Number.isFinite(mm) || mm < 0 || mm > 59) return undefined
    return Math.round(hh * 60 + mm)
  }
  const num = Number(v.replace(',', '.'))
  if (!Number.isFinite(num) || num < 0) return undefined
  return Math.round(num * 60)
}

/**
 * AP-26-Nachtrag — Spanne zwischen zwei `HH:MM`-Eingaben in Minuten.
 *
 * Gegenstück zu `spanMinutes` im Backend: Ein Ende, das nicht nach dem Beginn
 * liegt, gilt als am Folgetag (Nachtschicht). Gerechnet wird hier nur zur
 * **Anzeige** — gespeichert wird, was der Server daraus ableitet.
 */
function spanMinutes(von: string, bis: string): number | null {
  const teil = (v: string) => {
    const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(v.trim())
    return m ? Number(m[1]) * 60 + Number(m[2]) : null
  }
  const a = teil(von)
  const b = teil(bis)
  if (a === null || b === null) return null
  const roh = b - a
  return roh > 0 ? roh : roh + 24 * 60
}

/** Ganze Zahl aus einer Eingabe. Leer = nicht erfasst, `undefined` = unlesbar. */
function parseIntInput(raw: string): number | null | undefined {
  const v = raw.trim()
  if (v === '') return null
  const n = Number(v.replace(',', '.'))
  if (!Number.isFinite(n) || n < 0) return undefined
  return Math.round(n)
}

/** AP-26: Tagesdatensatz → Formularwerte der zehn getypten Felder. */
function dayFieldsToForm(r: DailyRecord | null): Record<string, string> {
  return {
    startTime: fmtTimeUTC(r?.startTime ?? ''),
    endTime: fmtTimeUTC(r?.endTime ?? ''),
    departure: fmtTimeUTC(r?.departure ?? ''),
    returnTime: fmtTimeUTC(r?.returnTime ?? ''),
    travelMinutes: r?.travelMinutes != null ? String(r.travelMinutes) : '',
    appointmentMinutes: r?.appointmentMinutes != null ? String(r.appointmentMinutes) : '',
    km: r?.km != null ? String(r.km) : '',
    appointment: r?.appointment ?? '',
    isDriver: String(r?.isDriver === true),
    overnight: String(r?.overnight === true),
  }
}

type FormFieldValue = string | number | boolean

interface FormState {
  [key: string]: FormFieldValue
}

export default function DailyRecordDialog({
  open,
  onClose,
  date,
  onSaved,
  inline = false,
  userId,
  manualHours = false,
  dayFields = [],
}: DailyRecordDialogProps) {
  const { t } = useTranslation()
  const hoursFormat = useHoursFormat()

  // State
  const [record, setRecord] = useState<DailyRecord | null>(null)
  const [customSchema, setCustomSchema] = useState<CustomFieldSchema[] | null>(null)
  const [formState, setFormState] = useState<FormState>({})
  const [groupRecords, setGroupRecords] = useState<Record<string, SelectionDataRecord[]>>({})
  const [loading, setLoading] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // AP-24: händisch erfasste Stunden. AP-26: die zehn getypten Tagesfelder,
  // alles als String — Ja/Nein als 'true'/'false' wie bei den Typ-B-Feldern.
  const [work, setWork] = useState('')
  const [pause, setPause] = useState('')
  const [df, setDf] = useState<Record<string, string>>({})

  // Load data when dialog opens
  useEffect(() => {
    if (!open) return

    const loadData = async () => {
      setLoading(true)
      setError(null)
      setGroupRecords({})
      try {
        // Fetch existing record and custom field schema in parallel.
        // getByDate returns 404 when no record exists yet — that is normal, so we
        // swallow 404 and treat every other status code as a real error.
        const [recordResponse, schemaResponse] = await Promise.all([
          dailyRecordsApi.getByDate(date, userId).catch((err: unknown) => {
            const status = (err as { response?: { status?: number } })?.response?.status
            if (status === 404) return null
            throw err // re-throw real errors so outer catch can handle them
          }),
          timeConfigApi.getCustomFieldsSchema(),
        ])

        const existingRecord = recordResponse?.data || null
        // Backend returns the schema array directly (or null/{} if not configured)
        const schema = Array.isArray(schemaResponse.data)
          ? schemaResponse.data
          : null

        setRecord(existingRecord)
        setCustomSchema(schema)

        // AP-24: null bleibt leeres Feld — „nicht erfasst" ist etwas anderes als
        // „0 Stunden" und darf nicht als 0 vorbelegt werden.
        setWork(existingRecord?.workMinutes != null ? formatHours(existingRecord.workMinutes, hoursFormat) : '')
        setPause(existingRecord?.breakMinutes != null ? formatHours(existingRecord.breakMinutes, hoursFormat) : '')
        setDf(dayFieldsToForm(existingRecord))

        // Initialize form state with existing data
        if (existingRecord?.customFields) {
          setFormState(existingRecord.customFields as FormState)
        } else {
          // Initialize with empty values based on schema
          const initialState: FormState = {}
          schema?.forEach((field) => {
            initialState[field.name] = field.type === 'checkbox' ? false : ''
          })
          setFormState(initialState)
        }

        // Fetch Vorgabedatensätze for all groups in schema
        if (schema) {
          const groupNames = [...new Set(schema.filter((f) => f.group).map((f) => f.group!))]
          groupNames.forEach((groupName) => {
            timeConfigApi.getSelectionDataRecords(groupName)
              .then(({ data }) => {
                setGroupRecords((prev) => ({
                  ...prev,
                  [groupName]: (data ?? []).filter((r) => r.isActive),
                }))
              })
              .catch(() => {/* Vorgabedaten sind optional – Fehler still ignorieren */})
          })
        }
      } catch (err) {
        console.error('Failed to load daily record data:', err)
        setError(
          t('timeTracking.errorLoadingData', {
            defaultValue: 'Fehler beim Laden der Daten',
          })
        )
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [open, date, userId, t, hoursFormat])

  // Handle field value change
  const handleFieldChange = (fieldName: string, value: FormFieldValue) => {
    setFormState((prev) => ({
      ...prev,
      [fieldName]: value,
    }))
  }

  // Save as DRAFT
  /**
   * AP-26-Nachtrag — sind Kommt UND Geht eingeschaltet, sind sie die Eingabe und
   * die Arbeitszeit fällt daraus ab. Ohne sie bleibt die Maske, wie sie war.
   */
  const zeitenAlsQuelle =
    manualHours && ['startTime', 'endTime'].every((k) => dayFields.some((f) => f.key === k))
  const spanne = zeitenAlsQuelle ? spanMinutes(df.startTime ?? '', df.endTime ?? '') : null

  /** Ein synchronisierter Datensatz ist nur noch Anzeige. */
  const isReadOnly = record?.status === 'SYNCED'

  const zeitFelder = dayFields.filter((f) => f.key === 'startTime' || f.key === 'endTime')
  const restFelder = dayFields.filter((f) => f.key !== 'startTime' && f.key !== 'endTime')

  const feldRahmen =
    'w-full rounded-md border border-border bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50'

  const renderTagesfeld = (f: DayFieldDef) => {
    const id = `dr-df-${f.key}`
    const set = (v: string) => setDf((p) => ({ ...p, [f.key]: v }))
    return (
      <div key={f.key} className={`space-y-2 ${f.kind === 'text' ? 'col-span-2' : ''}`}>
        <label htmlFor={id} className={fieldLabel}>
          {f.label}
          {zeitenAlsQuelle && (f.key === 'startTime' || f.key === 'endTime') && (
            <span className="text-destructive"> *</span>
          )}
        </label>
        {f.kind === 'boolean' ? (
          <input
            id={id}
            type="checkbox"
            checked={df[f.key] === 'true'}
            disabled={isReadOnly}
            onChange={(e) => set(String(e.target.checked))}
            className="h-4 w-4 accent-[hsl(var(--primary))]"
          />
        ) : (
          <input
            id={id}
            // `type="time"` gibt dem Browser die Uhrzeit-Tastatur und liefert
            // immer HH:MM — genau das Format, das der Server erwartet.
            type={f.kind === 'time' ? 'time' : 'text'}
            inputMode={f.kind === 'number' || f.kind === 'hours' ? 'decimal' : 'text'}
            value={df[f.key] ?? ''}
            placeholder="–"
            disabled={isReadOnly}
            onChange={(e) => set(e.target.value)}
            className={feldRahmen}
          />
        )}
      </div>
    )
  }

  /** Dauer-Eingabe (Arbeitszeit, Pause) — bewusst Textfeld, keine Uhrzeit. */
  const renderDauer = (label: string, id: string, value: string, setValue: (v: string) => void) => (
    <div key={id} className="space-y-2">
      <label htmlFor={id} className={fieldLabel}>{label}</label>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        value={value}
        placeholder="–"
        disabled={isReadOnly}
        onChange={(e) => setValue(e.target.value)}
        className={feldRahmen}
      />
    </div>
  )

  const handleSaveDraft = async () => {
    // AP-24: Stunden zuerst prüfen — sonst speicherte ein Tippfehler die
    // Zusatzfelder und verwürfe die Stunden stillschweigend.
    let hours: { workMinutes?: number | null; breakMinutes: number | null } | null = null
    if (zeitenAlsQuelle) {
      // Kommt und Geht sind Pflicht; `workMinutes` leitet der Server ab, damit
      // die Formel nicht in vier Masken einzeln steht.
      if (spanne === null) {
        setError('Bitte Kommt und Geht angeben.')
        return
      }
      const b = parseHoursInput(pause)
      if (b === undefined) {
        setError('Bitte die Pause als 0,5 oder 0:30 eingeben.')
        return
      }
      if ((b ?? 0) > spanne) {
        setError('Die Pause ist länger als die Anwesenheit.')
        return
      }
      hours = { breakMinutes: b ?? 0 }
    } else if (manualHours) {
      const w = parseHoursInput(work)
      const b = parseHoursInput(pause)
      if (w === undefined || b === undefined) {
        setError('Bitte Stunden als 8,5 oder 8:30 eingeben.')
        return
      }
      if ((w ?? 0) + (b ?? 0) > 1440) {
        setError('Arbeitszeit und Pause ergeben zusammen mehr als 24 Stunden.')
        return
      }
      hours = { workMinutes: w, breakMinutes: b }
    }

    // AP-26: nur die eingeschalteten Felder — was nicht im Payload steht, wird
    // nicht überschrieben (feldweises Schreiben).
    const ap26: Record<string, unknown> = {}
    for (const f of dayFields) {
      const raw = df[f.key] ?? ''
      if (f.kind === 'boolean') {
        ap26[f.key] = raw === 'true'
      } else if (f.kind === 'number') {
        const n = parseIntInput(raw)
        if (n === undefined) {
          setError(`Bitte ${f.label} als ganze Zahl eingeben.`)
          return
        }
        ap26[f.key] = n
      } else if (f.kind === 'hours') {
        const m = parseHoursInput(raw)
        if (m === undefined) {
          setError(`Bitte ${f.label} als 1,5 oder 1:30 eingeben.`)
          return
        }
        ap26[f.key] = m
      } else {
        ap26[f.key] = raw === '' ? null : raw
      }
    }

    setSaveLoading(true)
    setError(null)
    try {
      await dailyRecordsApi.upsert({
        date,
        ...((customSchema?.length ?? 0) > 0 && { customFields: formState }),
        ...(hours ?? {}),
        ...ap26,
      }, userId)
      onSaved?.()
      if (!inline) onClose()
    } catch (err) {
      console.error('Failed to save draft:', err)
      setError(
        apiErrorText(
          err,
          t('timeTracking.errorSavingRecord', {
            defaultValue: 'Fehler beim Speichern des Datensatzes',
          }),
        ),
      )
    } finally {
      setSaveLoading(false)
    }
  }

  // ── Render a single field ──────────────────────────────────────────────────
  // Wenn Optionen hinterlegt sind, wird das Feld immer als Dropdown gerendert
  // (außer Checkbox), unabhängig vom eingestellten Feldtyp.
  const renderSingleField = (field: CustomFieldSchema, isReadOnly: boolean) => {
    const value = formState[field.name] ?? (field.type === 'checkbox' ? false : '')
    const fieldId = `daily-${field.name}`

    // Options vorhanden → immer Dropdown (außer Checkbox)
    if (field.options && field.options.length > 0 && field.type !== 'checkbox') {
      return (
        <div key={field.name} className="space-y-2">
          <label htmlFor={fieldId} className={fieldLabel}>
            {field.label}
            {field.unit && <span className="ml-1 text-xs text-muted-foreground">({field.unit})</span>}
            {field.required && <span className="text-destructive"> *</span>}
          </label>
          <select
            id={fieldId}
            value={value as string}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            disabled={isReadOnly}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">Wählen…</option>
            {field.options.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      )
    }

    switch (field.type) {
      case 'text':
        return (
          <div key={field.name} className="space-y-2">
            <label
              htmlFor={fieldId}
              className={fieldLabel}
            >
              {field.label}
              {field.required && <span className="text-destructive"> *</span>}
            </label>
            <input
              id={fieldId}
              type="text"
              value={value as string}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              disabled={isReadOnly}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        )

      case 'number':
        return (
          <div key={field.name} className="space-y-2">
            <label
              htmlFor={fieldId}
              className={fieldLabel}
            >
              {field.label}
              {field.unit && <span className="ml-1 text-xs text-muted-foreground">({field.unit})</span>}
              {field.required && <span className="text-destructive"> *</span>}
            </label>
            <div className="flex items-center gap-2">
              <input
                id={fieldId}
                type="number"
                value={value as string | number}
                onChange={(e) =>
                  handleFieldChange(field.name, e.target.value ? parseFloat(e.target.value) : '')
                }
                disabled={isReadOnly}
                className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
              />
              {field.unit && (
                <span className="text-sm text-muted-foreground">{field.unit}</span>
              )}
            </div>
          </div>
        )

      case 'select':
        return (
          <div key={field.name} className="space-y-2">
            <label
              htmlFor={fieldId}
              className={fieldLabel}
            >
              {field.label}
              {field.required && <span className="text-destructive"> *</span>}
            </label>
            <select
              id={fieldId}
              value={value as string}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              disabled={isReadOnly}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">
                {t('timeTracking.selectOption', { defaultValue: 'Wählen...' })}
              </option>
              {field.options?.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        )

      case 'time':
        return (
          <div key={field.name} className="space-y-2">
            <label
              htmlFor={fieldId}
              className={fieldLabel}
            >
              {field.label}
              {field.required && <span className="text-destructive"> *</span>}
            </label>
            <input
              id={fieldId}
              type="time"
              value={typeof value === 'string' ? value : ''}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              disabled={isReadOnly}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        )

      case 'checkbox':
        return (
          <div key={field.name} className="flex items-center space-x-2">
            <input
              id={fieldId}
              type="checkbox"
              checked={value === true || value === 'true'}
              onChange={(e) => handleFieldChange(field.name, e.target.checked)}
              disabled={isReadOnly}
              className="h-4 w-4 rounded border border-border bg-background text-primary focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <label
              htmlFor={fieldId}
              className={`${fieldLabel} cursor-pointer`}
            >
              {field.label}
              {field.required && <span className="text-destructive"> *</span>}
            </label>
          </div>
        )

      default:
        return null
    }
  }

  // ── Render alle Felder mit Gruppen-Unterstützung ───────────────────────────
  // Felder mit gleichem group-Namen werden als Block mit Datensatz-Selektor gerendert.
  // Wird ein Vorgabedatensatz gewählt, werden alle Felder der Gruppe automatisch befüllt.
  const renderFieldsSection = (fields: CustomFieldSchema[], isReadOnly: boolean) => {
    const processedGroups = new Set<string>()
    const elements: JSX.Element[] = []

    for (const field of fields) {
      if (field.group) {
        if (processedGroups.has(field.group)) continue // Bereits als Teil des Blocks gerendert
        processedGroups.add(field.group)

        const groupFields = fields.filter((f) => f.group === field.group)
        const records = groupRecords[field.group] ?? []

        elements.push(
          <div key={`group-${field.group}`} className="space-y-3 rounded-md border border-border/60 bg-muted/20 p-3">
            {/* Gruppenname als Label */}
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {field.group}
            </p>

            {/* Datensatz-Selektor – nur wenn Vorgabedaten vorhanden und nicht read-only */}
            {records.length > 0 && !isReadOnly && (
              <div className="space-y-1">
                <label className="block text-xs text-muted-foreground">
                  Vorlage wählen (befüllt alle Felder automatisch)
                </label>
                <select
                  value=""
                  onChange={(e) => {
                    const rec = records.find((r) => r.id === e.target.value)
                    if (rec) {
                      groupFields.forEach((gf) => {
                        if (rec.values[gf.name] !== undefined) {
                          handleFieldChange(gf.name, rec.values[gf.name] as FormFieldValue)
                        }
                      })
                    }
                  }}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">– Vorlage auswählen –</option>
                  {records.map((rec) => {
                    const label = groupFields
                      .map((gf) => rec.values[gf.name])
                      .filter(Boolean)
                      .join(' · ')
                    return (
                      <option key={rec.id} value={rec.id}>{label || rec.id}</option>
                    )
                  })}
                </select>
              </div>
            )}

            {/* Einzelne Felder der Gruppe */}
            {groupFields.map((gf) => renderSingleField(gf, isReadOnly))}
          </div>
        )
      } else {
        // Nicht-gruppiertes Feld – normal rendern
        const el = renderSingleField(field, isReadOnly)
        if (el) elements.push(el)
      }
    }

    return elements
  }

  // Get status badge styling
  const getStatusBadgeStyle = (status: DailyRecordStatus) => {
    switch (status) {
      case 'DRAFT':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      case 'SUBMITTED':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      case 'SYNCED':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }

  if (!open) return null

  const inner = (
    <>
        {/* Header */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h2 className="text-xl font-semibold text-primary">
              {t('timeTracking.dailyRecord', { defaultValue: 'Tagesdaten' })}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">{date}</p>
          </div>
          {record?.status && (
            <span
              className={`inline-block rounded-full px-2 py-1 text-xs font-medium ${getStatusBadgeStyle(
                record.status
              )}`}
            >
              {record.status}
            </span>
          )}
        </div>

        {/* Loading state */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <p className="text-muted-foreground">
              {t('common.loading', { defaultValue: 'Wird geladen...' })}
            </p>
          </div>
        ) : (
          <>
            {/* Error message */}
            {error && (
              <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}

            {/*
              AP-26 — Ein Raster fuer den ganzen Tag, in fachlicher Lesereihenfolge:
              Kommt · Geht · Pause · restliche Tagesfelder · (darunter die Diaeten).

              Bis zum 9.9.2026 kamen Stunden und Tagesfelder aus zwei getrennten
              Bloecken, wodurch die Pause VOR Kommt und Geht stand — also vor den
              Werten, aus denen sie abgezogen wird.
            */}
            {(manualHours || dayFields.length > 0) && (
              <div className="mb-6">
                <div className="grid grid-cols-2 gap-4">
                  {zeitFelder.map(renderTagesfeld)}
                  {/* Die Arbeitszeit entfaellt, sobald sie aus Kommt und Geht
                      abfaellt — sie stuende sonst neben ihrer eigenen Herleitung. */}
                  {manualHours && !zeitenAlsQuelle &&
                    renderDauer('Arbeitszeit', 'dr-work', work, setWork)}
                  {manualHours && renderDauer('Pause', 'dr-break', pause, setPause)}
                  {restFelder.map(renderTagesfeld)}
                </div>
                {manualHours && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    {zeitenAlsQuelle
                      ? `Die Arbeitszeit ergibt sich aus Kommt und Geht abzüglich Pause${spanne !== null ? ` — derzeit ${formatHours(Math.max(0, spanne - (parseHoursInput(pause) ?? 0)), hoursFormat)}` : ''}. Ein Ende vor dem Beginn gilt als Folgetag.`
                      : '8,5 oder 8:30 — beides wird verstanden. Leer heißt „nicht erfasst": dann zählt für diesen Tag die gestempelte Zeit.'}
                  </p>
                )}
              </div>
            )}

            {/* Zusatzfelder Typ B (Diäten) */}
            {!customSchema || customSchema.length === 0 ? (
              // Der Hinweis gilt nur, wenn die Maske sonst leer wäre — mit
              // Stundenblock oder Tagesfeldern ist sie es nicht.
              !manualHours && dayFields.length === 0 && (
                <div className="rounded-md border border-border bg-background px-4 py-3 text-sm text-muted-foreground">
                  {t('timeTracking.noCustomFields', {
                    defaultValue:
                      'Keine Zusatzfelder konfiguriert. Der Admin kann diese in den Zeiterfassungs-Einstellungen festlegen.',
                  })}
                </div>
              )
            ) : (
              <div className="mb-6 space-y-4">
                {/* Überschrift nur, wenn darüber schon etwas steht: Ein Mandant mit
                    einem Diäten-Feld „Kilometer" hat sonst zweimal dasselbe Wort in
                    derselben Maske, ohne zu sehen, welches davon frei benannt ist. */}
                {(manualHours || dayFields.length > 0) && (
                  <h3 className="border-t border-border pt-4 text-sm font-medium text-foreground">
                    {t('timeTracking.dailyRecordFields', { defaultValue: 'Diäten' })}
                  </h3>
                )}
                {renderFieldsSection(customSchema, isReadOnly)}
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-3">
              {!inline && (
                <Button
                  onClick={onClose}
                  variant="outline"
                  className="flex-1 border-border text-foreground hover:bg-card"
                  disabled={saveLoading}
                >
                  {t('common.cancel', { defaultValue: 'Abbrechen' })}
                </Button>
              )}

              {!isReadOnly && (
                <Button
                  onClick={handleSaveDraft}
                  className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                  disabled={saveLoading}
                >
                  {saveLoading
                    ? t('common.saving', { defaultValue: 'Speichert...' })
                    : t('common.apply', { defaultValue: 'Übernehmen' })}
                </Button>
              )}
            </div>
          </>
        )}
    </>
  )

  if (inline) {
    return (
      <div className="rounded-lg border border-primary/30 bg-muted/20 p-6">
        {inner}
      </div>
    )
  }

  return (
    <Modal isOpen onClose={onClose} size="md" className="max-w-md" hideCloseButton>
      {inner}
    </Modal>
  )
}
